import {getFirstByProperty, remove} from 'shared/utils/array-utils.fp';
import type {Auth, Transaction, User} from 'typings/portaro.be.types';
import type {FactoryFunction} from 'typings/portaro.fe.types';
import type ReloaderService from '../../commons/services/reloader.service';
import type FinishedResponseInteractionService from '../../commons/services/finished-response-interaction.service';
import type TransactionDataService from '../transaction.data-service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type PaymentDataService from '../../commons/data-services/payment.data-service';
import {CASH_PAYMENT_PROVIDER_NAME, MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME} from './user-finance-panel.component';

export interface IUserFinanceService {
    loadAll();
    getAllNotZero();
    getAllDebts();
    remove(transaction);
    getAllByGroup(group);
    refresh();
    refreshSum();
    refreshGroups();
    isForgivable(transaction);
    forgive(transaction);
    payAllUserDebts(provider: string);
    getDebtsSum();
    getCreditsSum();
}

export interface UserFinanceServiceFactory {
    getUserFinanceService(owner: User, userCanDeletePayment: boolean, userCanDeleteDebt: boolean): IUserFinanceService;
}

/*@ngInject*/
export function UserFinanceServiceFactory(transactionDataService: TransactionDataService,
                                          paymentDataService: PaymentDataService,
                                          currentAuth: Auth,
                                          reloader: ReloaderService,
                                          finishedResponseInteractionService: FinishedResponseInteractionService) {

    class UserFinanceService implements IUserFinanceService {

        private all: Transaction[] = [];
        private groups: GroupTransaction[] = [];
        private sum = 0;
        private debtsSum = 0;
        private creditsSum = 0;
        private transactionResolved = false;

        constructor(private owner: User, private userCanDeletePayment: boolean, private userCanDeleteDebt: boolean) {
        }

        @ngAsync()
        public async loadAll() {
            try {
                this.all = await transactionDataService.query(this.owner.id);
                this.transactionResolved = true;
                this.refresh();
            } catch (errResponse) {
                finishedResponseInteractionService.showFailedInToast(errResponse);
            }
        }

        public getAllNotZero() {
            return this.all.filter((a) => a.sum !== 0);
        }

        public getAllDebts() {
            return this.all.filter((a) => a.debt);
        }

        public remove(transaction) {
            remove(transaction)(this.all);
        }

        public getAllByGroup(group) {
            return this.all
                .filter((a) => a.typeId === group.typeId);
        }

        public refresh() {
            this.refreshGroups();
            this.refreshSum();
        }

        public refreshSum() {
            this.sum = 0;
            this.debtsSum = 0;
            this.creditsSum = 0;
            this.all.forEach((a) => this.sum += a.sum);
            this.groups.forEach((g) => {
                if (g.sum < 0) {
                    this.debtsSum += g.sum;
                }
                if (g.sum > 0) {
                    this.creditsSum += g.sum;
                }
            });
        }

        public refreshGroups() {
            this.groups.forEach((g) => g.sum = 0);
            this.all.forEach((a) => {
                let groupFromList = getFirstByProperty<GroupTransaction, keyof GroupTransaction>('typeId', a.typeId)(this.groups);
                if (groupFromList === null) {
                    groupFromList = {
                        text: a.text,
                        sum: 0,
                        type: a.type,
                        typeId: a.typeId,
                        debt() {
                            return this.sum < 0;
                        }
                    };
                    this.groups.push(groupFromList);
                }
                groupFromList.sum += a.sum;
            });
        }

        public isForgivable(transaction) {
            if (transaction.sum === 0) {
                return false;
            }
            if (transaction.debt && this.userCanDeleteDebt) {
                return true;
            }
            if (transaction.payment && this.userCanDeletePayment) {
                return true;
            }
            return false;
        }

        @ngAsync()
        public async forgive(transaction: Transaction) {
            try {
                await transactionDataService.remove(transaction.id);
                transaction.sum = 0;
                this.refresh();
            } catch (errResponse) {
                finishedResponseInteractionService.showFailedInToast(errResponse);
            }
        }

        public async payAllUserDebts(provider: string) {
            const payRequest = {
                provider,
                payer: this.getPayer(provider),
                department: null,
                itemsSpec: {
                    owner: [this.owner],
                    directPaymentOwner: this.owner
                }
            };
            try {
                const response = await paymentDataService.pay(payRequest);
                await finishedResponseInteractionService.showSuccessInModalWindow(response);
            } catch(response) {
                await finishedResponseInteractionService.showFailedInModalWindow(response);
            }
            reloader.reload();
        }

        private getPayer(provider: string) {
            if ([CASH_PAYMENT_PROVIDER_NAME, MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME].includes(provider)) {
                return this.owner;
            }
            return currentAuth.activeUser;
        }

        public getDebtsSum() {
            return this.debtsSum;
        }

        public getCreditsSum() {
            return this.creditsSum;
        }
    }

    this.getUserFinanceService = (owner: User, userCanDeletePayment: boolean, userCanDeleteDebt: boolean) => {
        return new UserFinanceService(owner, userCanDeletePayment, userCanDeleteDebt);
    };
}

(UserFinanceServiceFactory as FactoryFunction).factoryName = 'userFinanceServiceFactory';

interface GroupTransaction extends Pick<Transaction, 'text' | 'sum' | 'type' | 'typeId'> {
    debt: () => boolean;
}